/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import {GenericObject} from '@ion/core/src/types/entities';
import {RenderFrame} from '@ion/dls/src/components/frame/RenderFrame';
import {toggleCollapseStyle} from '@ion/dls/src/components/navigation/useExpandCollapse';
import {DLSTheme} from '@ion/dls/src/types/types';
import useRootManager from './useRootManager';

/* 
 * Default name assigned to all containers of persistent iframes,
 * allowing easy targeting and removal from the DOM when needed.
 */
const IFRAME_CONTAINER_NAME = 'iFrameContainer';

// Default ID for persistent iframe
const DEFAULT_IFRAME_ID = 'iFrameId';

interface PersistentIFrameProps {
  url?: string,
  error?: boolean,
  loading?: boolean,
  iFrameId?: string,
  containerName?: string,
  persistIFrame?: boolean,
  isNavCollapsed?: boolean,
  style?: GenericObject
};
  
const useStyles = makeStyles<DLSTheme>((theme) => ({
  iframe : {
    width                          : toggleCollapseStyle('calc(100vw - 50px)', 'calc(100vw - 105px)'),
    height                         : 'calc(100vh - 45px)',
    position                       : 'absolute',
    top                            : '45px',
    left                           : toggleCollapseStyle('50px', '105px'),
    transition                     : 'width 0.2s ease-in-out, left 0.2s ease-in-out',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 103px)',
      width  : '100% !important',
      top    : '95px',
      left   : '0px !important'
    }
  }
}));

/**
 * The usePersistentIFrame hook provides a utility to store an iframe in the DOM,
 * allowing it to be reused without needing to reload.
 * 
 * ```typescript
 * import usePersistentIFrame from '@ion/dls/src/client/usePersistentIFrame';
 * 
 * usePersistentIFrame(params);
 * 
 * ```
 */
export const usePersistentIFrame = ({
  url,
  error,
  loading,
  style,
  containerName = IFRAME_CONTAINER_NAME,
  iFrameId = DEFAULT_IFRAME_ID,
  persistIFrame = false,
  isNavCollapsed = false
}: PersistentIFrameProps) => {
  const classes     = useStyles({collapsed: isNavCollapsed});
  const iFrameIdRef = useRef(iFrameId);
  
  const {
    render,
    getContainer,
    createContainer,
    removeContainer
  } = useRootManager(false);
  
  let iFrameContainer = persistIFrame ? getContainer(iFrameId) : null;

  const removePersistentIFrameById = useCallback(id => {
    const iframe = getContainer(id);
    removeContainer(iframe);
  }, []);

  const removePersistentIFrames = useCallback((containerName = IFRAME_CONTAINER_NAME) => {
    document.querySelectorAll(`div[name="${containerName}"]`)
      ?.forEach(iframe => removeContainer(iframe));
  }, []);

  useEffect(() => {
    iFrameIdRef.current = iFrameId;
  }, [iFrameId]);
    
  useEffect(() => {
    if (persistIFrame && url && !loading && !error) {
      const iframe = iFrameContainer?.querySelector('iframe') as HTMLIFrameElement | null;
      if (iFrameContainer && iframe && iframe.src !== url) {
        removeContainer(iFrameContainer);
        iFrameContainer = null;
      }
      if (!iFrameContainer) {
        iFrameContainer = createContainer(iFrameId);
        iFrameContainer.setAttribute('name', containerName);
        document.body.appendChild(iFrameContainer);
        render(
          <div className={style ? undefined : classes.iframe} style={style}>
            <RenderFrame url={url} scrolling='auto' style={{border: 'none'}} />
          </div>,
          iFrameContainer
        );
      } else {
        const childNode = iFrameContainer.children[0] as HTMLElement;
        if (childNode) {
          iFrameContainer.style.contentVisibility = 'visible';
          if (style) {
            childNode.style.width    = `${style.width}px`;
            childNode.style.height   = `${style.height}px`;
            childNode.style.top      = `${style.top}px`;
            childNode.style.left     = `${style.left}px`;
            childNode.style.position = 'absolute';
          } else {
            childNode.className = classes.iframe;
          }
        }
      }
    };
  }, [persistIFrame, containerName, iFrameId, loading, error, url, style]);
  
  useEffect(() => {
    return () => {
      const iframeContainer = getContainer(iFrameIdRef.current);
      iframeContainer && (iframeContainer.style.contentVisibility = 'hidden');
    };
  }, [persistIFrame]);

  return {removePersistentIFrameById, removePersistentIFrames};
};
  
export default usePersistentIFrame;
